import React, { useState } from 'react';
import { X, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Modal } from './Modal';
import { deleteContact } from '../lib/db';

interface ContactFormProps {
  contact?: {
    id: string;
    name: string;
    email: string;
    birthday: string | null;
    anniversary: string | null;
    card_preference: string;
    relationship: string;
    gender: 'male' | 'female' | null;
    address_line1?: string;
    address_city?: string;
    address_state?: string;
    address_zip?: string;
    notes?: string;
  };
  onClose: () => void;
  onSubmit: () => void;
  mode: 'add' | 'edit';
}

interface RelationshipOption {
  label: string;
  gender: 'male' | 'female' | null;
}

interface FamilyOptions {
  parents: RelationshipOption[];
  siblings: RelationshipOption[];
  children: RelationshipOption[];
  extended: RelationshipOption[];
  spouse: RelationshipOption[];
}

interface RelationshipOptions {
  Family: FamilyOptions;
  Friends: RelationshipOption[];
  Professional: RelationshipOption[];
  Other: RelationshipOption[];
}

const RELATIONSHIP_OPTIONS: RelationshipOptions = {
  Family: {
    parents: [
      { label: 'Mother', gender: 'female' },
      { label: 'Father', gender: 'male' }
    ],
    siblings: [
      { label: 'Sister', gender: 'female' },
      { label: 'Brother', gender: 'male' }
    ],
    children: [
      { label: 'Daughter', gender: 'female' },
      { label: 'Son', gender: 'male' }
    ],
    extended: [
      { label: 'Aunt', gender: 'female' },
      { label: 'Uncle', gender: 'male' },
      { label: 'Niece', gender: 'female' },
      { label: 'Nephew', gender: 'male' },
      { label: 'Grandmother', gender: 'female' },
      { label: 'Grandfather', gender: 'male' }
    ],
    spouse: [
      { label: 'Wife', gender: 'female' },
      { label: 'Husband', gender: 'male' }
    ]
  },
  Friends: [
    { label: 'Friend', gender: null }
  ],
  Professional: [
    { label: 'Colleague', gender: null },
    { label: 'Client', gender: null },
    { label: 'Business Partner', gender: null }
  ],
  Other: [
    { label: 'Other', gender: null }
  ]
};

export function ContactForm({ contact, onClose, onSubmit, mode }: ContactFormProps) {
  const [formData, setFormData] = useState({
    name: contact?.name || '',
    email: contact?.email || '',
    birthday: contact?.birthday || '',
    anniversary: contact?.anniversary || '',
    card_preference: contact?.card_preference || 'Traditional',
    relationship: contact?.relationship || 'Friend',
    gender: contact?.gender || null,
    address_line1: contact?.address_line1 || '',
    address_city: contact?.address_city || '',
    address_state: contact?.address_state || '',
    address_zip: contact?.address_zip || '',
    notes: contact?.notes || ''
  } as {
    name: string;
    email: string;
    birthday: string | null;
    anniversary: string | null;
    card_preference: string;
    relationship: string;
    gender: 'male' | 'female' | null;
    address_line1: string;
    address_city: string;
    address_state: string;
    address_zip: string;
    notes: string | null | undefined;
  });

  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState(() => {
    if (!contact?.relationship) return '';
    for (const [category, options] of Object.entries(RELATIONSHIP_OPTIONS)) {
      if (category === 'Family') {
        const familyOptions = options as FamilyOptions;
        for (const [_, groupOptions] of Object.entries(familyOptions)) {
          if (groupOptions.some((opt: RelationshipOption) => opt.label === contact.relationship)) {
            return category;
          }
        }
      } else {
        const categoryOptions = options as RelationshipOption[];
        if (categoryOptions.some((opt: RelationshipOption) => opt.label === contact.relationship)) {
          return category;
        }
      }
    }
    return '';
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    try {
      if (mode === 'edit' && contact) {
        const { error } = await supabase
          .from('contacts')
          .update(formData)
          .eq('id', contact.id);

        if (error) throw error;
      } else {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error('No active session');

        const { error } = await supabase
          .from('contacts')
          .insert([{
            ...formData,
            user_id: session.user.id,
            birthday: formData.birthday || null,
            anniversary: formData.anniversary || null
          }]);

        if (error) throw error;
      }

      onSubmit();
      onClose();
    } catch (error) {
      console.error('Error saving contact:', error);
      setError('Failed to save contact. Please try again.');
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={mode === 'add' ? 'Add Contact' : 'Edit Contact'}
      maxWidth="2xl"
    >
      <div className="p-4 sm:p-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Personal Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-brand-secondary">Personal Information</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-brand-secondary mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-secondary mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                  required
                />
              </div>
            </div>

            {/* Card Preferences */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-brand-secondary mb-1">
                  Card Preference *
                </label>
                <select
                  value={formData.card_preference}
                  onChange={(e) => setFormData({ ...formData, card_preference: e.target.value })}
                  className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                  required
                >
                  <option value="">Select Preference</option>
                  <option value="Traditional">Traditional</option>
                  <option value="Modern">Modern</option>
                  <option value="Funny">Funny</option>
                  <option value="Sentimental">Sentimental</option>
                  <option value="Inspirational">Inspirational</option>
                  <option value="Religious">Religious</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-secondary mb-1">
                  Relationship Category *
                </label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                  required
                >
                  <option value="">Select Category</option>
                  {Object.keys(RELATIONSHIP_OPTIONS).map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Relationship */}
            <div>
              <label className="block text-sm font-medium text-brand-secondary mb-1">
                Relationship *
              </label>
              <select
                value={formData.relationship}
                onChange={(e) => {
                  const selected = e.target.value;
                  const selectedOption = Object.values(RELATIONSHIP_OPTIONS)
                    .flat()
                    .find(option => option.label === selected);
                  setFormData(prev => ({
                    ...prev,
                    relationship: selected,
                    gender: selectedOption?.gender || null
                  }));
                }}
                className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                required
              >
                <option value="">Select Relationship</option>
                {selectedCategory && (selectedCategory === 'Family' ? (
                  Object.entries(RELATIONSHIP_OPTIONS.Family).map(([group, options]) => (
                    <optgroup key={group} label={group.charAt(0).toUpperCase() + group.slice(1)}>
                      {options.map((option: RelationshipOption) => (
                        <option key={option.label} value={option.label}>
                          {option.label}
                        </option>
                      ))}
                    </optgroup>
                  ))
                ) : (
                  (RELATIONSHIP_OPTIONS[selectedCategory as keyof typeof RELATIONSHIP_OPTIONS] as RelationshipOption[])?.map((option: RelationshipOption) => (
                    <option key={option.label} value={option.label}>
                      {option.label}
                    </option>
                  ))
                ))}
              </select>
            </div>
          </div>

          {/* Address */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-brand-secondary">Mailing Address</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-brand-secondary mb-1">
                  Street Address *
                </label>
                <input
                  type="text"
                  value={formData.address_line1}
                  onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
                  className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-secondary mb-1">
                  City *
                </label>
                <input
                  type="text"
                  value={formData.address_city}
                  onChange={(e) => setFormData({ ...formData, address_city: e.target.value })}
                  className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-secondary mb-1">
                  State *
                </label>
                <input
                  type="text"
                  value={formData.address_state}
                  onChange={(e) => setFormData({ ...formData, address_state: e.target.value })}
                  className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-secondary mb-1">
                  ZIP Code *
                </label>
                <input
                  type="text"
                  value={formData.address_zip}
                  onChange={(e) => setFormData({ ...formData, address_zip: e.target.value })}
                  className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                  required
                />
              </div>
            </div>
          </div>

          {/* Important Dates */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-brand-secondary">Important Dates</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-brand-secondary mb-1">
                  Birthday
                </label>
                <input
                  type="date"
                  value={formData.birthday || ''}
                  onChange={(e) => setFormData({ ...formData, birthday: e.target.value || null })}
                  className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-secondary mb-1">
                  Anniversary
                </label>
                <input
                  type="date"
                  value={formData.anniversary || ''}
                  onChange={(e) => setFormData({ ...formData, anniversary: e.target.value || null })}
                  className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-brand-secondary">Notes</h3>
            <div>
              <textarea
                value={formData.notes || ''}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value || null })}
                className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary min-h-[100px]"
                placeholder="Add any additional notes about this contact..."
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex flex-col sm:flex-row justify-between gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                if (mode === 'edit' && window.confirm('Are you sure you want to delete this contact?')) {
                  deleteContact(contact!.id);
                  onClose();
                }
              }}
              className={`w-full sm:w-auto px-6 py-3 text-base font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${mode === 'add' ? 'hidden' : ''}`}
            >
              Delete Contact
            </button>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={onClose}
                className="w-full sm:w-auto px-6 py-3 text-base font-medium text-brand-secondary bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="w-full sm:w-auto px-6 py-3 text-base font-medium text-white bg-brand-primary rounded-lg hover:bg-brand-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary"
              >
                {mode === 'add' ? 'Add Contact' : 'Save Changes'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
} 